import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Label } from './ui/label';
import {
    ColorPicker,
    ColorPickerSelection,
    ColorPickerHue,
    ColorPickerAlpha,
    ColorPickerFormat,
    ColorPickerEyeDropper,
    ColorPickerSwatch,
    ColorPickerOutput,
} from './ui/color-picker';

interface IconColorPickerProps {
    label: string;
    color: string;
    onChange: (color: string) => void;
}

export function IconColorPicker({
    label,
    color,
    onChange,
}: IconColorPickerProps) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [isMobile, setIsMobile] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const scrollableParentsRef = useRef<Element[]>([]);

    // Detekce mobilního zařízení
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const updatePosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();

            if (isMobile) {
                // Na mobilu centrovaný modal
                setPosition({
                    top: window.innerHeight / 2,
                    left: window.innerWidth / 2,
                });
            } else {
                // Na desktopu vedle tlačítka
                // Používáme getBoundingClientRect() bez scroll offsetu, protože fixed positioning je relativní k viewportu
                const padding = 8;
                setPosition({
                    top: rect.top,
                    left: rect.right + padding,
                });
            }
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        let rafId: number | null = null;
        const handleScroll = () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                updatePosition();
            });
        };

        if (isOpen) {
            // Použít mousedown i touchstart pro mobil
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
            if (!isMobile) {
                // Přidat listener na scroll pro window i všechny scrollovatelné rodičovské elementy
                window.addEventListener('scroll', handleScroll, true);
                // Najít všechny scrollovatelné rodičovské elementy a uložit reference
                scrollableParentsRef.current = [];
                let parent = buttonRef.current?.parentElement;
                while (parent) {
                    const overflow = window.getComputedStyle(parent).overflow;
                    if (overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') {
                        scrollableParentsRef.current.push(parent);
                        parent.addEventListener('scroll', handleScroll, true);
                    }
                    parent = parent.parentElement;
                }
            }
            window.addEventListener('resize', updatePosition);
            updatePosition();
        }

        return () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
            if (!isMobile) {
                window.removeEventListener('scroll', handleScroll, true);
                // Odstranit listenery z rodičovských elementů pomocí uložených referencí
                scrollableParentsRef.current.forEach(parent => {
                    parent.removeEventListener('scroll', handleScroll, true);
                });
                scrollableParentsRef.current = [];
            }
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, isMobile]);

    return (
        <>
            <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                    <Label className="text-sm font-semibold flex-1 text-boho-cream">{label}</Label>
                    <div className="flex items-center gap-2 relative">
                        {/* Color preview */}
                        <button
                            ref={buttonRef}
                            type="button"
                            onClick={() => setIsOpen(!isOpen)}
                            className="w-8 h-8 rounded border-2 border-border cursor-pointer hover:border-foreground/50 transition-colors"
                            style={{ backgroundColor: color }}
                            title={color}
                            aria-label={t('settings.colorLayers.selectColorFor', { label })}
                        />
                    </div>
                </div>
            </div>
            {/* Shadcn Color Picker Popup - vykreslený přes Portal */}
            {isOpen && typeof window !== 'undefined' && createPortal(
                <>
                    {/* Backdrop pro mobil */}
                    {isMobile && (
                        <div
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000]"
                            onClick={() => setIsOpen(false)}
                            onTouchStart={() => setIsOpen(false)}
                        />
                    )}
                    <div
                        ref={popoverRef}
                        className={isMobile ? "fixed z-[10001]" : "fixed z-[9999]"}
                        style={isMobile ? {
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                        } : {
                            top: `${position.top}px`,
                            left: `${position.left}px`,
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                    >
                        <ColorPicker
                            value={color}
                            onChange={(newColor) => {
                                if (import.meta.env.DEV) {
                                    // console.log('[IconColorPicker] ColorPicker onChange:', newColor, 'current color:', color);
                                }
                                onChange(newColor);
                            }}
                            className={`${isMobile ? 'w-full max-w-sm' : 'max-w-sm'} rounded-md border border-white/10 bg-[#1e1e1e]/95 backdrop-blur-lg p-4 shadow-lg text-boho-cream`}
                        >
                            <div className={isMobile ? "h-48 w-full" : "h-32 w-full"}>
                                <ColorPickerSelection />
                            </div>
                            <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center gap-4'}`}>
                                <ColorPickerEyeDropper />
                                <ColorPickerSwatch />
                                <div className="grid w-full gap-1">
                                    <ColorPickerHue />
                                    <ColorPickerAlpha />
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                <ColorPickerOutput />
                                <ColorPickerFormat />
                            </div>
                            {isMobile && (
                                <div className="mt-4 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => setIsOpen(false)}
                                        className="px-4 py-2 bg-green-dark text-white-pure rounded-lg font-semibold"
                                    >
                                        {t('common.apply', { defaultValue: 'Aplikovat' })}
                                    </button>
                                </div>
                            )}
                        </ColorPicker>
                    </div>
                </>,
                document.body
            )}
        </>
    );
}
